import { Component } from '@theme/component';

/**
 * <orivon-rail-component>: horizontal scroll-snap rail (design `.rail` + `.rail-progress`, plan §2.6).
 * - Progress bar: `--w` (visible share) and `--p` (scroll position) on the progress ref; hidden when nothing scrolls.
 * - Previous / next: buttons inside the component with an empty `data-o-rail-prev` / `data-o-rail-next`, or anywhere
 *   on the page with the attribute set to `#{component id}` (e.g. in the section head). They get aria-disabled at the ends.
 * - Desktop mouse drag (fine pointers), with the click after a drag suppressed.
 * `.o-rail--static-lg` turns the rail into a grid from 1024px: nothing scrolls there, so progress and controls go quiet.
 * Load it from the section: <script src="{{ 'orivon-rail.js' | asset_url }}" type="module" fetchpriority="low"></script>
 *
 * @example
 * <orivon-rail-component id="o-rail-{{ section.id }}">
 *   <div class="o-rail o-rail--static-lg" ref="track" style="--rail-cols: 3"> …cards… </div>
 *   <div class="o-rail-progress" ref="progress" aria-hidden="true"></div>
 * </orivon-rail-component>
 * <button class="o-btn o-btn--icon" type="button" data-o-rail-next="#o-rail-{{ section.id }}" aria-label="Next">…</button>
 *
 * @typedef {object} RailRefs
 * @property {HTMLElement} track - The scrolling .o-rail element.
 * @property {HTMLElement} [progress] - The .o-rail-progress element.
 *
 * @extends {Component<RailRefs>}
 */
export class OrivonRailComponent extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #frame = 0;
  #dragMoved = false;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const { track } = this.refs;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    document.addEventListener('click', this.#handleControlClick, { signal });

    this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
    this.#resizeObserver.observe(track);

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) this.#enableDrag(track, signal);

    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  /** Scrolls back by about one view. */
  previous() {
    this.#step(-1);
  }

  /** Scrolls on by about one view. */
  next() {
    this.#step(1);
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const first = track.firstElementChild;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
    const cardWidth = first ? first.getBoundingClientRect().width + gap : 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({
      left: direction * Math.max(cardWidth, track.clientWidth * 0.8),
      behavior: reduce ? 'auto' : 'smooth',
    });
  }

  /** @param {'prev' | 'next'} kind @returns {HTMLElement[]} */
  #controls(kind) {
    const attribute = kind === 'prev' ? 'data-o-rail-prev' : 'data-o-rail-next';
    /** @type {HTMLElement[]} */
    const inside = [...this.querySelectorAll(`[${attribute}=""]`)];
    /** @type {HTMLElement[]} */
    const outside = this.id ? [...document.querySelectorAll(`[${attribute}="#${CSS.escape(this.id)}"]`)] : [];
    return [...inside, ...outside];
  }

  /** @param {MouseEvent} event */
  #handleControlClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-o-rail-prev], [data-o-rail-next]');
    if (!control) return;

    const isPrev = control.hasAttribute('data-o-rail-prev');
    const value = control.getAttribute(isPrev ? 'data-o-rail-prev' : 'data-o-rail-next') || '';
    const mine = value ? Boolean(this.id) && value === `#${this.id}` : this.contains(control);
    if (!mine) return;

    event.preventDefault();
    if (control.getAttribute('aria-disabled') === 'true') return;
    if (isPrev) this.previous();
    else this.next();
  };

  #scheduleUpdate = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const { track, progress } = this.refs;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > 2;
    const position = Math.abs(track.scrollLeft);
    this.toggleAttribute('data-scrollable', scrollable);

    if (progress) {
      const share = Math.min(1, Math.max(0.08, track.clientWidth / (track.scrollWidth || 1)));
      progress.style.setProperty('--w', share.toFixed(3));
      progress.style.setProperty('--p', scrollable ? Math.min(1, position / max).toFixed(3) : '0');
      progress.hidden = !scrollable;
    }

    const atStart = !scrollable || position <= 2;
    const atEnd = !scrollable || position >= max - 2;
    this.#controls('prev').forEach((button) => button.setAttribute('aria-disabled', String(atStart)));
    this.#controls('next').forEach((button) => button.setAttribute('aria-disabled', String(atEnd)));
  }

  /** Mouse drag-to-scroll. @param {HTMLElement} track @param {AbortSignal} signal */
  #enableDrag(track, signal) {
    let down = false;
    let startX = 0;
    let startScroll = 0;

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        down = true;
        this.#dragMoved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      },
      { signal }
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (!down) return;
        const dx = event.clientX - startX;
        if (!this.#dragMoved && Math.abs(dx) > 5) {
          this.#dragMoved = true;
          track.classList.add('is-dragging');
        }
        if (this.#dragMoved) track.scrollLeft = startScroll - dx;
      },
      { passive: true, signal }
    );

    window.addEventListener(
      'pointerup',
      () => {
        if (!down) return;
        down = false;
        if (!this.#dragMoved) return;
        track.classList.remove('is-dragging');
        // Let the click that ends the drag be swallowed first.
        window.setTimeout(() => (this.#dragMoved = false), 0);
      },
      { signal }
    );

    track.addEventListener(
      'click',
      (event) => {
        if (!this.#dragMoved) return;
        event.preventDefault();
        event.stopPropagation();
      },
      { capture: true, signal }
    );

    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
  }
}

if (!customElements.get('orivon-rail-component')) {
  customElements.define('orivon-rail-component', OrivonRailComponent);
}
